import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Eye, EyeOff, ArrowLeft, Loader2, Mail, Check, X, Wrench } from 'lucide-react';
import { DeviceLimitDialog } from '../components/DeviceManager';
import { PhoneInput } from '../components/PhoneInput';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import { getApiBaseUrl } from '../lib/apiBase';
import { DEFAULT_COUNTRY_CODE } from '../lib/countryCodes';
import LegalAcceptCheckbox from '../components/LegalAcceptCheckbox';

const API_URL = getApiBaseUrl();

const isValidPhone = (phone) => /^\d{7,15}$/.test(phone);

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, loading: authLoading, isAdmin } = useAuth();
  const { t } = useTranslation();
  const [loginTab, setLoginTab] = useState('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [phoneError, setPhoneError] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [deviceLimitData, setDeviceLimitData] = useState(null);
  const [maintenanceInfo, setMaintenanceInfo] = useState(null);

  const from = location.state?.from?.pathname;

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate(from || (isAdmin ? '/admin' : '/dashboard'), { replace: true });
    }
  }, [user, authLoading, isAdmin, from, navigate]);

  const getIdentifier = () => (
    loginTab === 'email'
      ? { email }
      : { phone, phone_country_code: phoneCountryCode }
  );

  const attemptLogin = async () => {
    const loggedInUser = await login(getIdentifier(), password);
    navigate(from || (loggedInUser.role !== 'user' ? '/admin' : '/dashboard'), { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPhoneError('');
    setMaintenanceInfo(null);
    if (loginTab === 'phone' && !isValidPhone(phone)) {
      setPhoneError(t('login_phone_invalid', 'Enter a valid phone number (7-15 digits)'));
      return;
    }
    setLoading(true);
    try {
      await attemptLogin();
    } catch (err) {
      const errorData = err.response?.data?.detail;

      // Check if it's a device limit error with device list
      if (typeof errorData === 'object' && errorData.error === 'device_limit_exceeded') {
        setDeviceLimitData(errorData);
      } else if (typeof errorData === 'object' && errorData.reason === 'maintenance_mode') {
        setMaintenanceInfo(errorData);
      } else {
        setError(
          typeof errorData === 'string'
            ? errorData
            : t('login_failed', 'Login failed. Please try again.')
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutDevice = async (deviceId) => {
    // Use the force-logout endpoint, identifying by whichever tab (email or phone) was used to log in
    try {
      await axios.post(`${API_URL}/auth/devices/force-logout`, {
        ...getIdentifier(),
        password,
        device_id: deviceId
      });
      return true;
    } catch (err) {
      console.error('Force logout error:', err);
      throw err;
    }
  };

  const handleRetryLogin = async () => {
    setDeviceLimitData(null);
    setError('');
    setMaintenanceInfo(null);
    setLoading(true);
    try {
      await attemptLogin();
    } catch (err) {
      const errorData = err.response?.data?.detail;
      if (typeof errorData === 'object' && errorData.error === 'device_limit_exceeded') {
        setDeviceLimitData(errorData);
      } else if (typeof errorData === 'object' && errorData.reason === 'maintenance_mode') {
        setMaintenanceInfo(errorData);
      } else {
        setError(
          typeof errorData === 'string'
            ? errorData
            : t('login_failed', 'Login failed. Please try again.')
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#A67C52]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex" data-testid="login-page">
      <SEO
        title="Login — Thachari Interior Training"
        description="Login to access your interior technician and carpentry courses."
        url="/login"
        noIndex={true}
      />
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1761330439741-3dcf41ee766b?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt={t('login_image_alt', 'Interior design')}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 to-transparent" />
        <div className="relative z-10 p-12 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
              <span className="text-accent-foreground font-serif font-bold text-xl">T</span>
            </div>
            <span className="font-serif text-2xl font-medium text-foreground">THACHARI</span>
          </Link>
          <div className="max-w-md">
            <h2 className="text-3xl font-serif font-medium mb-4 text-foreground">
              {t('login_welcome_title', 'Welcome back to your learning journey')}
            </h2>
            <p className="text-muted-foreground">
              {t(
                'login_welcome_subtitle',
                'Continue mastering interior design and carpentry with expert-led courses.'
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 lg:hidden">
            <ArrowLeft className="w-4 h-4" />
            {t('auth_back_home', 'Back to Home')}
          </Link>

          <Card className="border-0 shadow-none lg:shadow-lg lg:border">
            {maintenanceInfo ? (
              <CardContent className="py-12 text-center" data-testid="maintenance-message">
                <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
                  <Wrench className="w-8 h-8 text-accent" />
                </div>
                <h2 className="text-2xl font-serif font-medium mb-3">
                  {t('login_maintenance_title', 'Under Maintenance')}
                </h2>
                <p className="text-muted-foreground mb-2">
                  {t(
                    'login_maintenance_body',
                    'Thachari is currently undergoing scheduled maintenance.'
                  )}
                </p>
                <p className="text-muted-foreground mb-8">
                  {t(
                    'login_maintenance_subbody',
                    "We'll be back shortly. Thank you for your patience."
                  )}
                </p>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('login_maintenance_signoff', '— The Thachari Team')}
                </p>
                <Button
                  variant="outline"
                  className="mt-8"
                  onClick={() => setMaintenanceInfo(null)}
                >
                  {t('login_maintenance_back', 'Back to Sign In')}
                </Button>
              </CardContent>
            ) : (
            <>
            <CardHeader className="space-y-1">
              <CardTitle className="text-3xl font-serif">{t('login_title', 'Sign In')}</CardTitle>
              <CardDescription>
                {t('login_subtitle', 'Enter your credentials to access your account')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" data-testid="login-error">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Tabs value={loginTab} onValueChange={(v) => { setLoginTab(v); setError(''); setPhoneError(''); setMaintenanceInfo(null); }}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="email" data-testid="login-tab-email">
                      {t('login_tab_email', 'Email')}
                    </TabsTrigger>
                    <TabsTrigger value="phone" data-testid="login-tab-phone">
                      {t('login_tab_phone', 'Phone')}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {loginTab === 'email' ? (
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('login_email_label', 'Email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('login_email_placeholder', 'you@example.com')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="login-email"
                      className="h-12"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="login-phone">{t('login_phone_label', 'Phone Number')}</Label>
                    <PhoneInput
                      countryCode={phoneCountryCode}
                      phone={phone}
                      onCountryCodeChange={setPhoneCountryCode}
                      onPhoneChange={setPhone}
                      error={phoneError}
                      testIdPrefix="login-phone"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password">{t('login_password_label', 'Password')}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('login_password_placeholder', 'Enter your password')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password"
                      className="h-12 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-12 w-12"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('login_signing_in', 'Signing in...')}
                    </>
                  ) : (
                    t('login_submit', 'Sign In')
                  )}
                </Button>

                <div className="text-right">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-muted-foreground hover:text-primary"
                    data-testid="forgot-password-link"
                  >
                    {t('login_forgot_password', 'Forgot password?')}
                  </Link>
                </div>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('login_no_account', "Don't have an account?")}{' '}
                  <Link to="/signup" className="text-accent hover:underline font-medium" data-testid="signup-link">
                    {t('login_sign_up_link', 'Sign up')}
                  </Link>
                </p>
              </div>

            </CardContent>
            </>
            )}
          </Card>
        </div>
      </div>

      {/* Device Limit Dialog */}
      <DeviceLimitDialog
        open={!!deviceLimitData}
        onClose={() => setDeviceLimitData(null)}
        devices={deviceLimitData?.devices || []}
        onLogoutDevice={handleLogoutDevice}
        onRetryLogin={handleRetryLogin}
      />
    </div>
  );
};

export const SignupPage = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Get referral code from URL
  const searchParams = new URLSearchParams(window.location.search);
  const refCode = searchParams.get('ref');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const errors = {};
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      errors.email = t('signup_email_invalid', 'Enter a valid email address');
    }
    if (!isValidPhone(phone)) {
      errors.phone = t('signup_phone_invalid', 'Enter a valid phone number (7-15 digits)');
    }
    if (password.length < 6) {
      errors.password = t('signup_password_short', 'Password must be at least 6 characters');
    }
    if (!acceptedTerms) {
      errors.terms = true;
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    try {
      await signup(email, password, name, phone, phoneCountryCode, refCode, acceptedTerms);
      if (window.fbq) {
        window.fbq('track', 'CompleteRegistration');
      }
      navigate('/dashboard');
    } catch (err) {
      setError(
        err.response?.data?.detail || t('signup_failed', 'Signup failed. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="signup-page">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1616592079624-575de673daff?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt={t('signup_image_alt', 'Carpentry workshop')}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 to-transparent" />
        <div className="relative z-10 p-12 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
              <span className="text-accent-foreground font-serif font-bold text-xl">T</span>
            </div>
            <span className="font-serif text-2xl font-medium text-foreground">THACHARI</span>
          </Link>
          <div className="max-w-md">
            <h2 className="text-3xl font-serif font-medium mb-4 text-foreground">
              {t('signup_journey_title', 'Start your journey to mastery')}
            </h2>
            <p className="text-muted-foreground">
              {t(
                'signup_journey_subtitle',
                'Join thousands of students learning interior design and carpentry from industry experts.'
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 lg:hidden">
            <ArrowLeft className="w-4 h-4" />
            {t('auth_back_home', 'Back to Home')}
          </Link>

          <Card className="border-0 shadow-none lg:shadow-lg lg:border">
            <CardHeader className="space-y-1">
              <CardTitle className="text-3xl font-serif">{t('signup_title', 'Create Account')}</CardTitle>
              <CardDescription>
                {t('signup_subtitle', 'Start your learning journey today')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" data-testid="signup-error">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="name">{t('signup_name_label', 'Full Name')}</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder={t('signup_name_placeholder', 'Your full name')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="signup-name"
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('signup_email_label', 'Email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('login_email_placeholder', 'you@example.com')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="signup-email"
                    className="h-12"
                  />
                  {fieldErrors.email && (
                    <p className="text-xs text-destructive">{fieldErrors.email}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-phone">{t('signup_phone_label', 'Phone Number')}</Label>
                  <PhoneInput
                    countryCode={phoneCountryCode}
                    phone={phone}
                    onCountryCodeChange={setPhoneCountryCode}
                    onPhoneChange={setPhone}
                    error={fieldErrors.phone}
                    testIdPrefix="signup-phone"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('signup_password_label', 'Password')}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('signup_password_placeholder', 'Min. 6 characters')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="signup-password"
                      className="h-12 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-12 w-12"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  {fieldErrors.password && (
                    <p className="text-xs text-destructive">{fieldErrors.password}</p>
                  )}
                </div>

                <LegalAcceptCheckbox
                  checked={acceptedTerms}
                  onChange={(v) => {
                    setAcceptedTerms(v);
                    if (v) setFieldErrors((prev) => ({ ...prev, terms: undefined }));
                  }}
                  error={!!fieldErrors.terms}
                  testId="signup-accept-terms"
                />

                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
                  disabled={loading}
                  data-testid="signup-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('signup_creating', 'Creating account...')}
                    </>
                  ) : (
                    t('signup_submit', 'Create Account')
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('signup_has_account', 'Already have an account?')}{' '}
                  <Link to="/login" className="text-accent hover:underline font-medium" data-testid="login-link">
                    {t('signup_sign_in_link', 'Sign in')}
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const ForgotPasswordPage = () => {
  const { t, tFormat } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [debugUrl, setDebugUrl] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(
        `${API_URL}/auth/forgot-password?email=${encodeURIComponent(email)}`
      );
      setSent(true);
      if (res.data.debug_reset_url) {
        setDebugUrl(res.data.debug_reset_url);
      }
    } catch (err) {
      toast.error(t('forgot_send_failed', 'Failed to send reset email. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(135deg, #F5E6D3 0%, #fff 50%, #E8D5C4 100%)' }}
    >
      <header className="py-4 px-8">
        <Link to="/" className="flex items-center gap-2">
          <span className="text-2xl font-serif font-bold tracking-wide text-primary">THACHARI</span>
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-serif">
              {t('forgot_title', 'Forgot Password')}
            </CardTitle>
            <CardDescription>
              {sent
                ? t('forgot_sent_desc', 'Check your email for a reset link')
                : t('forgot_desc', 'Enter your email to reset your password')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <p className="text-muted-foreground">
                  {tFormat(
                    'forgot_sent_body',
                    { email },
                    'If an account exists with {email}, you will receive a password reset link shortly.'
                  )}
                </p>
                {debugUrl && (
                  <div className="mt-4 p-4 bg-muted rounded-lg text-left">
                    <p className="text-xs text-muted-foreground mb-2">
                      {t('forgot_dev_only', 'Development only - Reset link:')}
                    </p>
                    <a href={debugUrl} className="text-sm text-primary underline break-all">
                      {debugUrl}
                    </a>
                  </div>
                )}
                <Link to="/login">
                  <Button variant="outline" className="mt-4">
                    {t('forgot_back_login', 'Back to Login')}
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="fp-email">{t('forgot_email_label', 'Email Address')}</Label>
                  <Input
                    id="fp-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('login_email_placeholder', 'you@example.com')}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {t('forgot_send_link', 'Send Reset Link')}
                </Button>
                <div className="text-center">
                  <Link to="/login" className="text-sm text-muted-foreground hover:text-primary">
                    {t('forgot_back_login', 'Back to Login')}
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const ResetPasswordPage = () => {
  const { t, tFormat } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidating(false);
        return;
      }

      try {
        const res = await axios.get(`${API_URL}/auth/verify-reset-token?token=${token}`);
        setTokenValid(res.data.valid);
        setEmail(res.data.email);
      } catch (err) {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t('reset_passwords_mismatch', 'Passwords do not match'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('signup_password_short', 'Password must be at least 6 characters'));
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API_URL}/auth/reset-password?token=${token}&new_password=${encodeURIComponent(password)}`
      );
      setSuccess(true);
      toast.success(t('reset_success_toast', 'Password reset successfully!'));
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('reset_failed', 'Failed to reset password'));
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(135deg, #F5E6D3 0%, #fff 50%, #E8D5C4 100%)' }}
    >
      <header className="py-4 px-8">
        <Link to="/" className="flex items-center gap-2">
          <span className="text-2xl font-serif font-bold tracking-wide text-primary">THACHARI</span>
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-serif">
              {t('reset_title', 'Reset Password')}
            </CardTitle>
            <CardDescription>
              {tokenValid
                ? tFormat(
                    'reset_desc_ok',
                    { email },
                    'Create a new password for {email}'
                  )
                : t('reset_desc_invalid', 'Invalid or expired link')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-muted-foreground">
                  {t(
                    'reset_success_body',
                    'Your password has been reset successfully. Redirecting to login...'
                  )}
                </p>
              </div>
            ) : !tokenValid ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
                  <X className="w-8 h-8 text-destructive" />
                </div>
                <p className="text-muted-foreground">
                  {t(
                    'reset_invalid_body',
                    'This password reset link is invalid or has expired. Please request a new one.'
                  )}
                </p>
                <Link to="/forgot-password">
                  <Button className="mt-4">{t('reset_request_new', 'Request New Link')}</Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="rp-password">{t('reset_new_password', 'New Password')}</Label>
                  <Input
                    id="rp-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('reset_new_password_ph', 'At least 6 characters')}
                    required
                    minLength={6}
                  />
                </div>
                <div>
                  <Label htmlFor="rp-confirm">{t('reset_confirm_password', 'Confirm Password')}</Label>
                  <Input
                    id="rp-confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('reset_confirm_ph', 'Confirm your password')}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {t('reset_submit', 'Reset Password')}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
